import { useRef, useState, type DragEvent, type JSX } from 'react'
import { useMutation } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import Link from '@mui/material/Link'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import type { CreateJobResponse, ZohoViewSummary } from '@profiler/shared'
import { useApi } from '../../auth'
import { ErrorPanel } from '../../components/States'
import { formatNumber } from '../../format'
import { useMe, useZohoViews } from '../../queries'

const ACCEPTED = ['.csv', '.xlsx', '.xls'] as const

/**
 * The Lambda proxy refuses a request over 6 MB, and the file is base64-encoded
 * into that request before Lambda sees it, so a third of the budget goes to the
 * encoding and the multipart envelope. A file above the real ceiling dies at the
 * proxy boundary, below our own JSON envelope, so the reply is an opaque gateway
 * error rather than the sentence under the drop zone — the pre-flight has to be
 * the stricter of the two limits, not the looser one.
 */
const MAX_BYTES = 4 * 1024 * 1024
const MAX_MB = MAX_BYTES / 1024 / 1024

interface EncodingNote {
  severity: 'success' | 'warning' | 'info'
  title: string
  detail: string
}

/**
 * Encoding is shown at full size, not as a footnote.
 *
 * Root cause D in the plan: the team's own file is ISO-8859-1, and the byte
 * where `Térence` should be renders as a replacement character in any modern
 * reader. The decode ladder recovers it silently, and the user needs to see that
 * it happened so they can check the sample rows on the next step.
 */
function encodingNote(encoding: string): EncodingNote {
  switch (encoding) {
    case 'utf-8':
    case 'utf-8-bom':
      return { severity: 'success', title: 'UTF-8', detail: 'Read directly, no fallback needed.' }
    case 'utf-16le':
    case 'utf-16be':
      return {
        severity: 'info',
        title: `UTF-16 (${encoding === 'utf-16le' ? 'little' : 'big'} endian)`,
        detail: 'Detected from the byte pattern and decoded without loss.',
      }
    case 'cp1252':
      return {
        severity: 'warning',
        title: 'Windows-1252 (ISO-8859-1)',
        detail:
          'Not UTF-8. Accented names were recovered by the fallback decoder — check them in the sample rows on the next step before continuing.',
      }
    default:
      return { severity: 'info', title: encoding, detail: 'Decoded without replacement characters.' }
  }
}

export function StepUpload({
  created,
  onCreated,
}: Readonly<{
  created: CreateJobResponse | null
  onCreated: (response: CreateJobResponse, file: File) => void
}>): JSX.Element {
  const api = useApi()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  /*
   * The second way in: a Zoho CRM view. Offered only while the CRM is
   * connected, because a picker over nothing is a question with no answer.
   * The job it makes is the same job — the rows land where an upload's do
   * and the wizard carries on — with one difference the wizard cannot see:
   * its accepted people are pushed to the CRM as they are accepted.
   */
  const me = useMe()
  const zohoConnected = me.data?.zoho?.connected === true
  const [source, setSource] = useState<'file' | 'zoho'>('file')
  const [view, setView] = useState<ZohoViewSummary | null>(null)
  const [viewName, setViewName] = useState('')
  const views = useZohoViews(zohoConnected && source === 'zoho')
  const fromZoho = useMutation({
    mutationFn: (chosen: ZohoViewSummary) =>
      api.createJobFromZoho({ cvid: chosen.id, name: viewName.trim() || chosen.name }),
    // The wizard takes the job's name from the file it was handed; a view has no file, so one carries the name.
    onSuccess: (response) => onCreated(response, new File([], `${response.name}.zoho`)),
  })

  // The sample is fetched rather than linked: every API route sits behind the
  // JWT authorizer and an anchor sends no Authorization header.
  const sample = useMutation({
    mutationFn: (format: 'xlsx' | 'csv') => api.downloadTemplate(format),
  })

  const upload = useMutation({
    mutationFn: (file: File) => api.createJob(file),
    onSuccess: (response, file) => onCreated(response, file),
  })

  function accept(file: File | undefined): void {
    // A second file while the first upload is still in flight would create a
    // second job: whichever response lands last is the one the wizard carries
    // forward, and the other is stranded as a DRAFT nothing can reach. The
    // button is disabled for this reason; the drop zone has to refuse it here.
    if (upload.isPending) return
    setLocalError(null)
    if (!file) return
    const lower = file.name.toLowerCase()
    if (!ACCEPTED.some((ext) => lower.endsWith(ext))) {
      setLocalError(`${file.name} is not a CSV or Excel file.`)
      return
    }
    if (file.size > MAX_BYTES) {
      setLocalError(`${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${MAX_MB} MB.`)
      return
    }
    upload.mutate(file)
  }

  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    setDragging(false)
    accept(event.dataTransfer.files[0])
  }

  const note = created ? encodingNote(created.encoding) : null

  return (
    <Stack spacing={2}>
      {zohoConnected ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={source}
          onChange={(_event, next: 'file' | 'zoho' | null) => {
            if (next) setSource(next)
          }}
        >
          <ToggleButton value="file">Upload a file</ToggleButton>
          <ToggleButton value="zoho">From a Zoho CRM view</ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      {source === 'zoho' ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Pick an Accounts view in Zoho CRM. Its accounts become the rows of this job, with the Zoho account
            id carried through to the export, and the people accepted here are sent back to the CRM under
            their accounts as they are accepted.
          </Typography>
          {views.isError ? <ErrorPanel error={views.error} onRetry={() => void views.refetch()} /> : null}
          <Autocomplete
            options={views.data?.views ?? []}
            value={view}
            loading={views.isLoading}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            groupBy={(option) =>
              option.isDefault ? 'Default' : (option.category ?? 'Views').replaceAll('_', ' ')
            }
            onChange={(_event, next) => {
              setView(next)
              if (next && !viewName) setViewName(next.name)
            }}
            renderInput={(params) => (
              <TextField {...params} label="Accounts view" placeholder="Start typing a view name" />
            )}
          />
          <TextField
            label="Job name"
            size="small"
            value={viewName}
            onChange={(event) => setViewName(event.target.value)}
            helperText="Defaults to the view's name."
          />
          <Box>
            <Button
              variant="contained"
              startIcon={<CloudDownloadOutlinedIcon />}
              disabled={!view || fromZoho.isPending}
              onClick={() => {
                if (view) fromZoho.mutate(view)
              }}
            >
              {fromZoho.isPending ? 'Reading the view' : 'Create the job from this view'}
            </Button>
          </Box>
          {fromZoho.isPending ? <LinearProgress /> : null}
          {fromZoho.isError ? <ErrorPanel error={fromZoho.error} /> : null}
        </Stack>
      ) : (
        <Box
          onDragOver={(event) => {
            event.preventDefault()
            if (upload.isPending) return
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: dragging ? 'primary.main' : 'divider',
            bgcolor: dragging ? 'action.hover' : 'background.paper',
            borderRadius: 2,
            p: 5,
            textAlign: 'center',
            transition: 'border-color 120ms, background-color 120ms',
          }}
        >
          <Stack spacing={1.5} alignItems="center">
            <UploadFileOutlinedIcon fontSize="large" color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Drop a company list here
            </Typography>
            <Typography variant="body2" color="text.secondary">
              CSV or Excel, up to {MAX_MB} MB. Account Name is the only required column.
            </Typography>
            <Button variant="outlined" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
              Choose a file
            </Button>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pt: 0.5 }}>
              <DownloadOutlinedIcon fontSize="inherit" color="action" />
              <Typography variant="body2" color="text.secondary">
                Not sure of the format?{' '}
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  disabled={sample.isPending}
                  onClick={() => sample.mutate('xlsx')}
                >
                  Download a sample
                </Link>{' '}
                (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  disabled={sample.isPending}
                  onClick={() => sample.mutate('csv')}
                >
                  or as CSV
                </Link>
                )
              </Typography>
            </Stack>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={ACCEPTED.join(',')}
              onChange={(event) => {
                accept(event.target.files?.[0])
                // Cleared so re-picking the same file after a rejection still fires change.
                event.target.value = ''
              }}
            />
          </Stack>
        </Box>
      )}

      {upload.isPending ? (
        <Box>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Uploading and decoding
          </Typography>
        </Box>
      ) : null}

      {localError ? <Alert severity="error">{localError}</Alert> : null}
      {upload.isError ? <ErrorPanel error={upload.error} /> : null}
      {sample.isError ? <ErrorPanel error={sample.error} /> : null}

      {created && note ? (
        <>
          <Alert severity={note.severity} icon={false}>
            <AlertTitle sx={{ fontSize: '1rem' }}>Detected encoding: {note.title}</AlertTitle>
            {note.detail}
          </Alert>

          <Alert severity="info" icon={false}>
            <AlertTitle>{created.name}</AlertTitle>
            {formatNumber(created.rowCount)} rows, {formatNumber(created.headers.length)} columns detected.
          </Alert>

          {created.warnings.length > 0 ? (
            <Alert severity="warning">
              <AlertTitle>Parser warnings</AlertTitle>
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {created.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </>
      ) : null}
    </Stack>
  )
}
